/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import { getErrorFromEndpoint, isStrEmptyOrNull } from '../UtilityFunctions';

const useGetEventDate = (loanNumber = '', dateCode = '', occurrence = '') => {
  const { get } = useAbortController();
  const { token } = useAuthContext();

  const [eventDateInfo, setEventDateInfo] = useState<UnknownEventResp>(null);
  const [eventDateStatus, setEventDateStatus] = useState('done');

  const getEventDateInfo = useCallback(
    async (
      loanNo: string = loanNumber,
      dateCde: string = dateCode,
      occr: string = occurrence,
    ): Promise<EventObj | null> => {
      if (!token || !loanNo || !dateCde) return;
      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
      };

      try {
        setEventDateStatus('loading');
        let url = '';
        if (occr) {
          url = `live/loan/${loanNo}/event/${dateCde}/occurrence/${occr}`;
        } else {
          url = `live/loan/${loanNo}/event/${dateCde}`;
        }
        let response = await axiosCall({ url: url, method: 'get' }, config, headers);
        const err = getErrorFromEndpoint(response);
        !isStrEmptyOrNull(err) ? setEventDateStatus('error') : setEventDateStatus('done');
        setEventDateInfo(response as UnknownEventResp);
        return !isStrEmptyOrNull(err) ? null : (response as EventObj);
      } catch (e) {
        if (e.code !== 'ERR_CANCELED') {
          console.error('Unable to load event date info: ' + e);
          setEventDateStatus('error');
        }
        return null;
      }
    },
    [get, loanNumber, dateCode, occurrence, token],
  );

  useEffect(() => {
    if (!isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(dateCode)) {
      getEventDateInfo();
    }
  }, [getEventDateInfo, loanNumber, dateCode]);

  return {
    eventDateInfo,
    eventDateStatus,
    getEventDateInfo,
  };
};

export default useGetEventDate;
